import { FC, useMemo, useState } from 'react';
import { useFavTeam, useRemoveFavTeam, useTeammenu } from './apis';
import CustomSelect, { ISelect } from '../formComponents/customSelect';
import { eliminateHtmlTags } from '../shared/utils';
import { ChakraStylesConfig, components } from 'chakra-react-select';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useTheme,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  AiFillHeart,
  AiOutlineFileSearch,
  AiOutlineHeart,
} from 'react-icons/ai';
import { IoSettingsOutline } from 'react-icons/io5';
import { useToastHook } from '../appToast';
import Loader from '../appLoader';
import { flattenTeamsWithMetadata } from '../shared/utils/teamHierarchy';
import { useTeamHierarchy } from '../hooks/useTeamHierarchy';
import { TeamHierarchySelectOption } from '../TeamHierarchySelectOption';

interface TeamMenuProps {
  selectedTeam: ISelect;
  handleSelectedTeam: (selectedTeam: ISelect) => void;
  mb?: number;
  noDefault?: boolean;
  hideShowDetails?: boolean;
  AuthActionTypes?: any;
  getUser?: any;
  dispatch?: any;
  appState?: any;
  actionHandler?: any;
  prefixContent?: React.ReactNode;
  prefixIcon?: any;
  teams?: string[];
  fullHeight?: boolean;
  hideLabel?: boolean;
}
export const TeamMenu: FC<TeamMenuProps> = ({
  selectedTeam,
  handleSelectedTeam,
  noDefault,
  mb,
  hideShowDetails,
  appState,
  dispatch,
  getUser,
  AuthActionTypes,
  actionHandler,
  prefixContent,
  prefixIcon,
  teams,
  fullHeight,
  hideLabel,
}) => {
  const navigate = useNavigate();
  const { colors } = useTheme();

  const [newToast] = useToastHook();
  const { data: teamData, isLoading } = useTeammenu();
  const [getUserLoading, setGetUserLoading] = useState(false);
  const { mutate: handleFavTeam, isLoading: handleFavTeamLoading } =
    useFavTeam();
  const { mutate: removeFavTeam, isLoading: removeFavTeamLoading } =
    useRemoveFavTeam();

  const allTeams = useMemo(
    () =>
      flattenTeamsWithMetadata(teamData?.dto ?? []).map((t) => ({
        ...t,
        id: t.value,
        subTeams: [] as any[],
        parentPath: '',
      })),
    [teamData?.dto]
  );

  const {
    expandedTeamPaths,
    toggleTeamExpand,
    visibleTeams: teamsOptions,
  } = useTeamHierarchy(allTeams);

  const baseOption = { value: '', label: 'Organization', id: 'all' };

  const options = noDefault ? teamsOptions : [baseOption, ...teamsOptions];

  const targetSubTeam = options?.filter(
    (team: any) =>
      team.options &&
      team.options?.some(
        (subTeam: any) => subTeam.value === appState?.user?.dto?.team
      )
  );

  const targetTeam = options?.filter(
    (el: any) =>
      el.value === appState?.user?.dto?.team ||
      el.value === targetSubTeam[0]?.value
  );

  const newOptions = [
    baseOption,
    ...(targetTeam || []),
    ...options.filter(
      (el: any) => el.value !== targetTeam[0]?.value && el.value !== ''
    ),
  ];

  const onChange = actionHandler
    ? actionHandler
    : (selected: string) =>
        handleSelectedTeam({
          value: eliminateHtmlTags(selected),
          label:
            eliminateHtmlTags(selected) === ''
              ? 'Organisation'
              : eliminateHtmlTags(selected),
        });

  const chakraStyles: ChakraStylesConfig = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided, state) => {
      const depth: number = (state.data as any)?.depth ?? 0;
      return {
        ...provided,
        paddingLeft: `${12 + depth * 20}px`,
        cursor: 'pointer',
        backgroundColor: state.isSelected
          ? '#4d4dff'
          : provided.backgroundColor,
        color: state.isSelected ? 'white' : provided.color,
        _hover: {
          backgroundColor: state.isSelected ? '#4d4dff' : '#F6F7F9',
          color: state.isSelected ? 'white' : provided.color,
        },
      };
    },
  };

  const flattenTeams = (teams: any[]) => {
    return teams?.flatMap((team) => {
      return [team, ...team.subTeams];
    });
  };
  const flattenedArray = flattenTeams(teamData?.dto);

  const filteredOptions = flattenedArray
    ?.filter((item: any) => teams?.includes(item.name))
    ?.map((el: any) => ({ value: el.name, label: el.name, id: el.name }));

  return (
    <Flex align="flex-end" mb={mb}>
      <CustomSelect
        size={prefixContent || fullHeight ? 'md' : 'sm'}
        placeholder="Organisation"
        label={
          prefixContent || fullHeight || hideLabel
            ? ''
            : actionHandler ?? 'Team'
        }
        chakraStyles={chakraStyles}
        defaultItem={selectedTeam}
        expandedTeamPaths={expandedTeamPaths}
        toggleTeamExpand={toggleTeamExpand}
        components={
          prefixContent
            ? {
                GroupHeading: () => <div></div>,
                Option: TeamHierarchySelectOption,
                SingleValue: ({ children, ...props }: any) => {
                  return (
                    <components.SingleValue {...props}>
                      <Flex gap={2}>
                        {prefixIcon}
                        <Text
                          fontFamily="heading"
                          fontSize="sm"
                          color="text.primary"
                        >
                          {`${prefixContent}: `}
                        </Text>
                        <Text
                          fontWeight="semibold"
                          fontFamily="heading"
                          fontSize="sm"
                          color="text.primary"
                        >
                          {children}
                        </Text>
                      </Flex>
                    </components.SingleValue>
                  );
                },
              }
            : {
                GroupHeading: () => <div></div>,
                Option: TeamHierarchySelectOption,
              }
        }
        onChange={onChange}
        options={
          teams
            ? [{ label: 'Organisation', value: '', id: 'all' }].concat(
                filteredOptions
              )
            : newOptions
        }
        getOptionValue={(option: any) => `${option.id}`}
      />

      {selectedTeam.value && !hideShowDetails && (
        <HStack ml={3} spacing={3}>
          <Box height="32px" width="1.5px" bg="gray.300" />

          <Tooltip hasArrow label="More Details">
            <IconButton
              aria-label="more details"
              size="sm"
              bg="transparent"
              border="1px solid"
              borderColor={'#CBD5E0'}
              onClick={() => navigate(`/teams/${selectedTeam.value}`)}
              icon={
                <AiOutlineFileSearch size={24} color={colors.text.secondary} />
              }
            />
          </Tooltip>

          <Tooltip hasArrow label="Edit Team">
            <IconButton
              bg="transparent"
              border="1px solid"
              borderColor="#CBD5E0"
              size="sm"
              onClick={() =>
                navigate(
                  `/settings/teams/${decodeURIComponent(selectedTeam.value)}`
                )
              }
              aria-label="Search database"
              icon={
                <IoSettingsOutline size={24} color={colors.text.secondary} />
              }
            />
          </Tooltip>

          <Tooltip
            hasArrow
            label={
              decodeURIComponent(selectedTeam.value) ===
              appState?.user?.dto?.team
                ? 'Remove from Favourite Team'
                : 'Mark as Favourite Team'
            }
          >
            <IconButton
              bg="transparent"
              size="sm"
              border="1px solid"
              borderColor="#CBD5E0"
              aria-label="Favorite"
              icon={
                decodeURIComponent(selectedTeam.value) ===
                appState?.user?.dto?.team ? (
                  <AiFillHeart size={24} color={colors.text.secondary} />
                ) : (
                  <AiOutlineHeart size={24} color={colors.text.secondary} />
                )
              }
              onClick={() => {
                decodeURIComponent(selectedTeam.value) ===
                appState?.user?.dto?.team
                  ? removeFavTeam(
                      {
                        name: decodeURIComponent(selectedTeam.value),
                      },
                      {
                        onSuccess: () => {
                          setGetUserLoading(true);
                          getUser()
                            .then((res: any) => {
                              setGetUserLoading(false);
                              dispatch({
                                type: AuthActionTypes.AUTH_READY,
                                payload: { ...appState.user, ...res },
                              });
                            })
                            .catch((err: any) => {
                              newToast({
                                message: 'Something went wrong',
                                status: 'error',
                              });
                              setGetUserLoading(false);
                            });
                        },
                      }
                    )
                  : appState?.user?.dto?.team
                  ? newToast({
                      message: `${appState?.user?.dto?.team} is marked as Favourite. Remove it from favourite and try again.`,
                      status: 'error',
                    })
                  : handleFavTeam(
                      {
                        name: decodeURIComponent(selectedTeam.value),
                      },
                      {
                        onSuccess: () => {
                          setGetUserLoading(true);
                          getUser()
                            .then((res: any) => {
                              setGetUserLoading(false);
                              dispatch({
                                type: AuthActionTypes.AUTH_READY,
                                payload: { ...appState.user, ...res },
                              });
                            })
                            .catch((err: any) => {
                              newToast({
                                message: 'Something went wrong',
                                status: 'error',
                              });
                              setGetUserLoading(false);
                            });
                        },
                      }
                    );
              }}
            />
          </Tooltip>
        </HStack>
      )}

      {(handleFavTeamLoading || removeFavTeamLoading || getUserLoading) && (
        <Loader />
      )}
    </Flex>
  );
};
